import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { MemberRole, type Box, type Member } from 'models/Box';
import { openActionableModal, closeModal } from 'xstate/actors/ModalActor';
import { ActionableModalPayload } from 'xstate/machines/ModalMachine';
import { requestToSelectBox } from 'xstate/actors/SpaceActor';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import useBoxMembers from 'networking/mindfeed/hooks/useBoxMembers';
import useBoxes from 'networking/mindfeed/hooks/useBoxes';
import useLeaveBox from 'networking/mindfeed/hooks/useLeaveBox';
import { buildSpacePath } from 'config/routing';
import { useRouter } from 'core/i18n/navigation';
import Button from 'elements/Button';
import Divider from 'elements/Divider';
import { useTranslations } from 'next-intl';

interface SharingSettingsFormProps {
  box: Box;
}

const SharingSettingsForm = ({ box }: SharingSettingsFormProps) => {
  const t = useTranslations();
  const { data: members, isLoading } = useBoxMembers({ boxId: box.id });
  const { data: boxes } = useBoxes();
  const { selectedBox, selectedSpace } = useActiveSpace();
  const leaveBoxMutation = useLeaveBox();
  const router = useRouter();

  const owner = members?.find((member) => member.role === MemberRole.Owner);
  const guests =
    members?.filter((member) => member.role !== MemberRole.Owner) ?? [];

  const navigateToFallbackBox = () => {
    // Pick the first box that isn't the on the user left
    const fallback = boxes?.find((b) => b.id !== box.id);

    if (fallback && selectedSpace) {
      requestToSelectBox(fallback);
      router.push(
        buildSpacePath({
          kind: 'timeline',
          spaceId: selectedSpace.id,
          boxId: fallback.id,
        }),
      );
    }
  };

  const onLeaveClick = () => {
    const payload: ActionableModalPayload = {
      title: t('boxSharing.leave.title'),
      bodyElement: (
        <div className="my-5">
          <span className="block my-2">
            {t.rich('boxSharing.leave.message', {
              name: box.name,
              b: (chunks) => <span className="font-semibold">{chunks}</span>,
            })}
          </span>
          <span className="text-red-500 my-2 block">
            {t('boxSharing.leave.warning')}
          </span>
        </div>
      ),
      onAccept: () => {
        leaveBoxMutation.mutate(
          { boxId: box.id },
          {
            onSuccess: () => {
              closeModal();
              if (selectedBox?.id === box.id) {
                navigateToFallbackBox();
              }
            },
          },
        );
      },
      acceptLabel: t('boxSharing.leave.action'),
    };
    openActionableModal(payload);
  };

  return (
    <div className={styles.container}>
      <section>
        <p className={styles.sectionLabel}>{t('roles.owner')}</p>
        {isLoading ? (
          <MemberRowSkeleton />
        ) : owner ? (
          <MemberRow member={owner} />
        ) : null}
      </section>

      {(isLoading || guests.length > 0) && (
        <section className="mt-2">
          <p className={styles.sectionLabel}>{t('boxSharing.guests')}</p>
          {isLoading ? (
            <>
              <MemberRowSkeleton />
              <MemberRowSkeleton />
            </>
          ) : (
            guests.map((member) => (
              <MemberRow key={member.id} member={member} />
            ))
          )}
        </section>
      )}

      <Divider className="mt-2 mb-4" />

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.leaveRow.container}
          onClick={onLeaveClick}
          disabled={leaveBoxMutation.isPending}
        >
          <span className={styles.leaveRow.iconWrap} aria-hidden="true">
            <FontAwesomeIcon
              icon={faArrowRightFromBracket}
              className={styles.leaveRow.icon}
            />
          </span>
          <span className={styles.leaveRow.label}>
            {t('boxSharing.leave.action')}
          </span>
        </button>

        <Button
          type="button"
          size="sm"
          className="bg-mf-blue px-6 rounded-full hover:bg-mf-blue-600 active:bg-mf-blue-700"
          onClick={closeModal}
        >
          {t('common.done')}
        </Button>
      </div>
    </div>
  );
};

type MemberRowProps = {
  member: Member;
};

const MemberRow = ({ member }: MemberRowProps) => {
  return (
    <div className={styles.member.container}>
      <div className={styles.member.avatar} aria-hidden="true" />
      <p className={styles.member.name}>{member.displayName}</p>
    </div>
  );
};

const MemberRowSkeleton = () => (
  <div className={styles.member.container}>
    <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
    <div className="ml-3 h-4 w-36 bg-gray-200 rounded animate-pulse" />
  </div>
);

const styles = {
  container: 'flex flex-col bg-white px-4',
  sectionLabel: 'mb-3 text-[15px] text-[#6C727E]',
  member: {
    container: 'mb-4 flex items-center',
    avatar: 'h-10 w-10 rounded-full bg-[#D4B5AD] flex-shrink-0',
    name: 'ml-3 text-[15px] font-bold text-[#161E3A]',
  },
  actions: 'flex items-center justify-between',
  leaveRow: {
    container:
      'flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed',
    iconWrap:
      'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-50',
    icon: 'text-red-500 w-4 h-4',
    label: 'text-base text-[#161E3A]',
  },
};

export default SharingSettingsForm;
